import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAppSelector } from "@/reducers/hooks"
import {
  selectAllWorkflows,
  WorkflowJob,
} from "@/reducers/openclawWorkflowsSlice"
import {
  defaultPhaseFor,
  describeBroaderScope,
  messageOf,
  nameFor,
  phaseOf,
  rulesForStop,
  StopPhase,
} from "@/lib/stopAutomations"
import {
  createStopAutomation,
  deleteStopAutomation,
  updateStopAutomation,
} from "@/api/xeps/StopAutomations"
import { useAutomationMutation } from "@/hooks/useAutomationMutation"
import { PHASE_LABEL_KEY, StopAutomationFields } from "./StopAutomationFields"
import { useTranslation } from "react-i18next"

export type StopAutomationDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Undefined when opened with a `stops` picker and nothing chosen yet —
   * Save stays disabled until the dispatcher picks one.
   */
  stopId?: string
  stopAction?: string
  /** Company + address. Shown in the dialog title only when there's no
   * picker — with a picker, the "Stop" field already shows it, and echoing
   * it in the title too would be redundant. */
  stopLabel?: string
  /** The route's driver JID; the agent matches it against the vehicle room too. */
  targetJid?: string
  /**
   * The full route, so the caller can offer a stop picker instead of a fixed
   * target — used when opened from a place that doesn't already imply one
   * stop (e.g. the driver-chat Automations widget), as opposed to a single
   * stop's own "..." menu, where the target is already unambiguous. Starts
   * unselected (no default stop) so the dispatcher must choose one.
   */
  stops?: { id: string; label: string }[]
  onStopChange?: (stopId: string) => void
}

export const StopAutomationDialog: React.FC<StopAutomationDialogProps> = ({
  open,
  onOpenChange,
  stopId,
  stopAction,
  stopLabel,
  targetJid,
  stops,
  onStopChange,
}) => {
  const { t } = useTranslation()
  const workflows = useAppSelector(selectAllWorkflows)
  const { pinned, broader } = useMemo(
    () => rulesForStop(workflows, stopId ?? ""),
    [workflows, stopId],
  )

  const [editingId, setEditingId] = useState<string | null>(null)
  const [phase, setPhase] = useState<StopPhase>(defaultPhaseFor(stopAction))
  const [message, setMessage] = useState("")
  const { saving, error, setError, run } = useAutomationMutation()

  const resetForm = useCallback(() => {
    setEditingId(null)
    setPhase(defaultPhaseFor(stopAction))
    setMessage("")
  }, [stopAction])

  // Reopening for another stop must not inherit the previous stop's draft.
  useEffect(() => {
    if (open) {
      resetForm()
      setError(null)
    }
  }, [open, stopId, resetForm, setError])

  const save = useCallback(async () => {
    const trimmed = message.trim()
    if (!trimmed || !stopId || !stopLabel) return

    const ok = await run(
      (client) =>
        editingId
          ? updateStopAutomation(client, editingId, {
              message: trimmed,
              phase,
              name: nameFor(stopLabel, phase),
            })
          : createStopAutomation(client, {
              stopId,
              phase,
              message: trimmed,
              name: nameFor(stopLabel, phase),
              stopAction:
                stopAction === "pickup" || stopAction === "delivery"
                  ? stopAction
                  : undefined,
              targetJid,
            }),
      resetForm,
    )
    if (ok) {
      resetForm()
      onOpenChange(false)
    }
  }, [
    editingId,
    message,
    onOpenChange,
    phase,
    resetForm,
    run,
    stopAction,
    stopId,
    stopLabel,
    targetJid,
  ])

  const remove = useCallback(
    async (id: string) => {
      const ok = await run(
        (client) => deleteStopAutomation(client, id),
        () => {
          if (editingId === id) resetForm()
        },
      )
      if (editingId === id) resetForm()
      if (ok) onOpenChange(false)
    },
    [editingId, onOpenChange, resetForm, run],
  )

  const startEditing = useCallback(
    (job: WorkflowJob) => {
      setEditingId(job.id)
      setPhase(phaseOf(job) ?? "arrival")
      setMessage(messageOf(job))
      setError(null)
    },
    [setError],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[1020] max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {stops
              ? t("maps.stopAutomationDialog.title")
              : t("maps.stopAutomationDialog.titleForStop", {
                  stop: stopLabel,
                })}
          </DialogTitle>
          <DialogDescription>
            {t("maps.stopAutomationDialog.description")}
          </DialogDescription>
        </DialogHeader>

        {stops && stops.length > 0 && (
          <div className="space-y-1">
            <Label>{t("maps.stopAutomationDialog.stop")}</Label>
            <Select
              value={stopId ?? ""}
              onValueChange={onStopChange}
              disabled={saving}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t("maps.stopAutomationDialog.chooseStop")}
                />
              </SelectTrigger>
              <SelectContent>
                {stops.map((stop) => (
                  <SelectItem key={stop.id} value={stop.id}>
                    {stop.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {pinned.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              {t("maps.stopAutomationDialog.setForStop")}
            </p>
            {pinned.map((job) => (
              <div
                key={job.id}
                className="flex items-start gap-2 rounded-md border p-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{job.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t(PHASE_LABEL_KEY[phaseOf(job) ?? "arrival"])}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={saving}
                  onClick={() => startEditing(job)}
                >
                  {t("common.edit")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-600"
                  disabled={saving}
                  onClick={() => remove(job.id)}
                >
                  {t("common.delete")}
                </Button>
              </div>
            ))}
          </div>
        )}

        {broader.length > 0 && (
          <div className="space-y-2 opacity-60">
            <p className="text-xs font-medium text-muted-foreground">
              {t("maps.stopAutomationDialog.alsoApplies")}
            </p>
            {broader.map((job) => (
              <div key={job.id} className="rounded-md border p-2">
                <p className="truncate text-sm">{job.name}</p>
                <p className="text-xs text-muted-foreground">
                  {t("maps.stopAutomationDialog.changeInList", {
                    scope: describeBroaderScope(job, t),
                  })}
                </p>
              </div>
            ))}
          </div>
        )}

        <StopAutomationFields
          phase={phase}
          onPhaseChange={setPhase}
          message={message}
          onMessageChange={setMessage}
          disabled={saving}
          error={error}
        />

        <DialogFooter>
          {editingId && (
            <Button variant="ghost" disabled={saving} onClick={resetForm}>
              {t("maps.stopAutomationDialog.cancelEdit")}
            </Button>
          )}
          <Button
            onClick={save}
            disabled={saving || !message.trim() || !stopId}
          >
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default StopAutomationDialog
